/**
 * AuthGate — the boot guard. On mount it attempts a silent refresh; on
 * success it renders the protected app shell. On failure it stashes the
 * intended path and redirects the browser to the centralized login.
 *
 * Mounting the AuthGate at the root of authenticated routes means a deep-
 * link load lands the user back where they wanted to go after sign-in.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { bootRefresh, redirectToCentralizedLogin } from './silentRefresh';
import { getAccessToken } from './authStore';
import { useAuth } from './AuthContext';
import { useCopy } from '@/hooks/useCopy';

type GateState = 'booting' | 'authed' | 'redirecting';

export function AuthGate(): ReactNode {
  const location = useLocation();
  const { setAuthed, refineOperator } = useAuth();
  const [state, setState] = useState<GateState>(() => (getAccessToken() ? 'authed' : 'booting'));
  const started = useRef(false);
  const { t } = useCopy();

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // The already-have-a-token short-circuit moves inside the async IIFE so the
    // effect body holds no synchronous setState. It still runs first (before any
    // await), so an already-authed mount transitions to 'authed' exactly as
    // before — and the lazy `useState` initializer already renders 'authed' from
    // the first frame when a token exists. No auth/refresh behavior changes.
    void (async () => {
      if (getAccessToken()) {
        setState('authed');
        return;
      }
      const ok = await bootRefresh();
      if (ok) {
        // Pull the freshly-stored token through context so consumers re-render.
        const token = getAccessToken();
        if (token) setAuthed(token, null);
        // Refine the claims-derived identity against GET /api/auth/me (U3).
        void refineOperator();
        setState('authed');
      } else {
        setState('redirecting');
        redirectToCentralizedLogin(location.pathname + location.search);
      }
    })();
  }, [location.pathname, location.search, setAuthed, refineOperator]);

  if (state === 'authed') {
    return <Outlet />;
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2,
      }}
    >
      <LoadingSpinner />
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {state === 'redirecting'
          ? t('auth.redirecting', 'Redirecting to sign in…')
          : t('app.loading', 'Loading')}
      </Typography>
    </Box>
  );
}
